import { create } from 'zustand';
import { fetchCandidates, buy, sell, fetchHoldings } from '../api/trades';
import type { CandidateResponse } from '../api/trades';
import { fetchAccount } from '../api/summary';
import type { Candidate, Holding, Trade, Account } from '../types';
import { isTradingTime } from '../utils/constants';

/**
 * 交易 Store
 * 管理：候选股 + 持仓 + 交易 + 账户
 */
interface TradeState {
  /** 当日候选股 */
  candidates: Candidate[];
  /** 候选股数据日期 */
  candidateDate: string;
  /** 候选股数据来源描述 */
  candidateSource: string;
  /** 持仓列表 */
  holdings: Holding[];
  /** 账户信息 */
  account: Account | null;
  /** 加载状态 */
  loading: boolean;
  /** 操作中状态 */
  actionLoading: boolean;
  /** 错误信息 */
  error: string | null;
  /** 持仓轮询定时器 */
  pollTimer: number | null;
  /** 候选轮询定时器 */
  candidateTimer: number | null;

  /** 获取候选股 */
  fetchCandidates: (date?: string) => Promise<void>;
  /** 获取持仓 */
  fetchHoldings: () => Promise<void>;
  /** 获取账户信息 */
  fetchAccount: () => Promise<void>;
  /** 买入 */
  buy: (code: string, price: number, quantity: number) => Promise<Trade>;
  /** 卖出 */
  sell: (holdingId: number, price?: number, quantity?: number) => Promise<Trade>;
  /** 启动轮询 */
  startPolling: () => void;
  /** 停止轮询 */
  stopPolling: () => void;
}

export const useTradeStore = create<TradeState>((set, get) => ({
  candidates: [],
  candidateDate: '',
  candidateSource: '',
  holdings: [],
  account: null,
  loading: false,
  actionLoading: false,
  error: null,
  pollTimer: null,
  candidateTimer: null,

  fetchCandidates: async (date?: string) => {
    set({ error: null });
    try {
      const res: CandidateResponse = await fetchCandidates(date);
      set({
        candidates: res.candidates,
        candidateDate: res.date,
        candidateSource: res.source,
      });
    } catch (error: any) {
      set({ error: error.message });
    }
  },

  fetchHoldings: async () => {
    set({ loading: true, error: null });
    try {
      const holdings = await fetchHoldings('held');
      set({ holdings, loading: false });
    } catch (error: any) {
      set({ loading: false, error: error.message });
    }
  },

  fetchAccount: async () => {
    try {
      const account = await fetchAccount();
      set({ account });
    } catch (error: any) {
      set({ error: error.message });
    }
  },

  buy: async (code: string, price: number, quantity: number) => {
    set({ actionLoading: true, error: null });
    try {
      const trade = await buy(code, price, quantity);
      // 刷新持仓和账户
      await get().fetchHoldings();
      await get().fetchAccount();
      set({ actionLoading: false });
      return trade;
    } catch (error: any) {
      set({ actionLoading: false, error: error.message });
      throw error;
    }
  },

  sell: async (holdingId: number, price?: number, quantity?: number) => {
    set({ actionLoading: true, error: null });
    try {
      const trade = await sell(holdingId, price, quantity);
      // 刷新持仓和账户
      await get().fetchHoldings();
      await get().fetchAccount();
      set({ actionLoading: false });
      return trade;
    } catch (error: any) {
      set({ actionLoading: false, error: error.message });
      throw error;
    }
  },

  startPolling: () => {
    const { pollTimer, candidateTimer } = get();

    // 立即拉取
    get().fetchHoldings();
    get().fetchCandidates();
    get().fetchAccount();

    // 自调度持仓轮询（交易时间 5s / 非交易时间 30s）
    if (!pollTimer) {
      const schedule = () => {
        const interval = isTradingTime() ? 5000 : 30000;
        const timer = window.setTimeout(async () => {
          await get().fetchHoldings();
          schedule();
        }, interval);
        set({ pollTimer: timer });
      };
      schedule();
    }

    // 自调度候选股轮询（交易时间 10s / 非交易时间 60s）
    if (!candidateTimer) {
      const schedule = () => {
        const interval = isTradingTime() ? 10000 : 60000;
        const timer = window.setTimeout(async () => {
          await get().fetchCandidates();
          schedule();
        }, interval);
        set({ candidateTimer: timer });
      };
      schedule();
    }
  },

  stopPolling: () => {
    const { pollTimer, candidateTimer } = get();
    if (pollTimer) {
      clearTimeout(pollTimer);
      set({ pollTimer: null });
    }
    if (candidateTimer) {
      clearTimeout(candidateTimer);
      set({ candidateTimer: null });
    }
  },
}));
